import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import {
  BottomSheet,
  Input,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";
import { useRef } from "react";

import { IconButton } from "../../components/IconButton";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { useWorkplacesSelection } from "./useWorkplacesSelection";
import { WorkplacesSelectionList } from "./WorkplacesSelectionList";

interface WorkplacesBottomSheetProps {
  modalState: UseModalState;
}

export function WorkplacesBottomSheet(props: WorkplacesBottomSheetProps) {
  const { modalState } = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    searchTerm,
    setSearchTerm,
    mode,
    maxFavoriteCount,
    itemGroups,
    isLoading,
    isError,
    isFetchingNextPage,
    handleLoadMore,
  } = useWorkplacesSelection();

  useLogEffect(
    APP_V2_USER_EVENTS.FAVORITE_WORKPLACES_PICKER_PAGE_VIEWED,
    {},
    {
      enabled: !isLoading && modalState.modalIsOpen,
    }
  );

  useLogErrorEffect(
    APP_V2_USER_EVENTS.FAVORITE_WORKPLACES_ERROR,
    {
      error: new Error(
        "Favorite workplaces picker data failed to load. Check RUM session for debugging."
      ),
    },
    {
      enabled: isError,
    }
  );

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        height: `calc(100% - ${getSafeAreaPaddings({ top: true }).paddingTop})`,
      }}
    >
      <DialogContent
        sx={{
          gap: 5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack sx={{ position: "relative", pt: 3 }}>
          <Stack spacing={3}>
            <Text semibold variant="h5" align="center">
              Choose workplaces
            </Text>
            <Text
              semibold
              align="center"
              variant="caption"
              color={(theme) => theme.palette.text.secondary}
            >
              select up to {maxFavoriteCount} favorite {pluralize("workplace", maxFavoriteCount)}
            </Text>
          </Stack>
          <IconButton
            size="small"
            variant="outlined"
            iconType="close"
            sx={(theme) => ({
              boxShadow: theme.shadows[2],
              outlineColor: theme.palette.border?.subtle,
              position: "absolute",
              right: 0,
              top: 0,
            })}
            onClick={() => {
              modalState.closeModal();
            }}
          />
        </Stack>

        <Input
          placeholder="Search for a workplace"
          iconType="search"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
        />

        <Stack
          sx={(theme) => ({
            height: "100%",
            minHeight: theme.size?.bottomSheet.loading.minHeight,
            justifyContent: isLoading ? "center" : "flex-start",
          })}
        >
          <WorkplacesSelectionList
            scrollRef={scrollRef}
            itemGroups={itemGroups}
            maxFavoriteCount={maxFavoriteCount}
            isLoading={isLoading}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            analytics={{ mode }}
            onLoadMore={handleLoadMore}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
